import styled, { css } from 'styled-components';

import { withAlpha } from 'services/ThemeTokens';

import { CHART_TONES, ChartTone } from './chartTokens';

/**
 * ТЗ 0103 — вёрстка общих графиков. Молекула не может брать токены страниц
 * (`_detailShared/*.styled` — уровень страниц), поэтому здесь те же значения
 * цветов и отступов, что у `DS.Table` / `S.Card`, — одна система на глаз.
 */

const TEXT = 'var(--k-fg-0f172a)';
const MUTED = 'var(--k-fg-64748b)';
const LINE = 'var(--k-border-e2e8f0)';
const TRACK = 'var(--k-bg-f1f5f9)';
const FOCUS = 'var(--k-fg-2563eb)';
const ACCENT = CHART_TONES.accent;

/** Холст chart.js растягивается на высоту карточки (`ChartCard`). */
export const ChartBox = styled.div`
  position: relative;
  height: 100%;
  min-height: 0;
`;

export const ChartEmpty = styled.div`
  padding: 12px 0;
  color: ${MUTED};
  font-size: 12px;
`;

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const SrCaption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/* ── Спарклайн ───────────────────────────────────────────────────────── */

export const SparkSvg = styled.svg`
  display: inline-block;
  vertical-align: middle;
  overflow: visible;
  flex: 0 0 auto;
`;

/* ── Полосы (RankBars) и воронка (FunnelBars) ────────────────────────── */

export const RankList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FunnelList = styled(RankList)`
  gap: 6px;
`;

export const RankItem = styled.li`
  min-width: 0;
`;

const rowFace = css`
  display: block;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  text-align: left;
  font: inherit;
  color: ${TEXT};
`;

export const RankStatic = styled.div`
  ${rowFace};
`;

export const RankButton = styled.button<{ $selected?: boolean }>`
  ${rowFace};
  border: 0;
  background: ${({ $selected }) =>
    $selected ? 'var(--k-bg-eff6ff)' : 'transparent'};
  cursor: pointer;

  &:hover {
    background: var(--k-bg-f8fafc);
  }

  &:focus-visible {
    outline: 2px solid ${FOCUS};
    outline-offset: -2px;
  }
`;

export const RankHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
`;

export const RankLabel = styled.span<{ $highlight?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ $highlight }) => ($highlight ? 700 : 500)};
`;

export const RankValue = styled.span<{ $muted?: boolean }>`
  flex: 0 0 auto;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ $muted }) => ($muted ? MUTED : TEXT)};
`;

const TONE_TEXT: Record<ChartTone, string> = {
  good: 'var(--k-fg-15803d)',
  bad: 'var(--k-fg-b91c1c)',
  warn: 'var(--k-fg-b45309)',
  neutral: MUTED,
  accent: 'var(--k-fg-1d4ed8)',
};

export const RankDelta = styled.span<{ $tone: ChartTone }>`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $tone }) => TONE_TEXT[$tone]};
`;

export const RankTrack = styled.div`
  position: relative;
  height: 8px;
  margin-top: 4px;
  border-radius: 4px;
  background: ${TRACK};
`;

export const RankFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 4px;
  background: ${({ $color }) => $color ?? ACCENT};
`;

/** Отметка сравнения — тонкая тёмная черта поперёк дорожки. */
export const RankCompare = styled.span<{ $at: number }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ $at }) => `calc(${$at}% - 1px)`};
  width: 2px;
  border-radius: 1px;
  background: ${TEXT};
  opacity: 0.55;
`;

export const RankSecondary = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${MUTED};
`;

export const FunnelShare = styled.span`
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
`;

export const FunnelTrack = styled.div`
  position: relative;
  height: 14px;
  margin-top: 4px;
  border-radius: 4px;
  background: ${TRACK};
`;

export const FunnelFill = styled.div<{ $width: number; $color?: string }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${({ $width }) => $width}%;
  border-radius: 3px;
  background: ${({ $color }) => $color ?? ACCENT};
`;

/** Сравнение — бледный столбик на всю высоту дорожки под текущим. */
export const FunnelCompare = styled.div<{ $width: number; $color?: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $width }) => $width}%;
  border-radius: 4px;
  background: ${({ $color }) => withAlpha($color ?? ACCENT, 0.35)};
`;

/* ── Тепловая матрица ────────────────────────────────────────────────── */

/** Одна синяя шкала, как у матрицы «канал × курс»; текст тёмный везде. */
export const HEAT_TONE_BG: ReadonlyArray<string> = [
  'transparent',
  'var(--k-bg-f5f9ff)',
  'var(--k-bg-e8f1fe)',
  'var(--k-bg-d6e6fd)',
  'var(--k-bg-c7dcfb)',
];

const heatBg = (tone: number): string =>
  HEAT_TONE_BG[Math.max(0, Math.min(HEAT_TONE_BG.length - 1, tone))];

export const HeatScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const HeatTable = styled.table<{ $compact?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border-bottom: 1px solid ${LINE};
  }

  /* ТЗ 0113: компактная матрица (виджет ИИ-чата) — колонки делят ширину
     карточки поровну и сжимаются, подписи колонок переносятся; не влезает
     (минимальная ширина задаётся по числу колонок) — прокрутка вбок внутри
     обёртки, а не обрезанная последняя колонка. */
  ${({ $compact }) =>
    $compact &&
    css`
      table-layout: fixed;
      font-size: 12.5px;

      th {
        white-space: normal;
        overflow-wrap: anywhere;
        padding: 6px 6px;
      }

      thead th {
        text-align: center;
      }

      td {
        min-width: 0;
        text-align: center;
      }

      td > span,
      td > button {
        padding: 6px 4px;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `}
`;

/** Ширина колонки строк компактной матрицы, px. */
export const HEAT_COMPACT_ROW_HEAD = 64;
/** Минимум ширины колонки значений компактной матрицы, px. */
export const HEAT_COMPACT_COL_MIN = 44;

/**
 * Финальная проверка 0103 (F-E14): колонка строк матрицы закреплена при
 * прокрутке вбок (когорты на 1100 — как таблица «Рекламы»): подпись строки
 * видна у любой колонки.
 */
const heatSticky = css`
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--k-bg-ffffff);
  box-shadow: inset -1px 0 0 ${LINE};
`;

export const HeatCorner = styled.th<{ $compact?: boolean }>`
  ${heatSticky}
  width: ${({ $compact }) =>
    $compact ? `${HEAT_COMPACT_ROW_HEAD}px` : '100%'};
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${MUTED};
  white-space: nowrap;
`;

export const HeatHead = styled.th<{ $highlight?: boolean }>`
  padding: 8px 12px;
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $highlight }) => ($highlight ? TEXT : MUTED)};
  white-space: nowrap;
  ${({ $highlight }) =>
    $highlight &&
    css`
      background: var(--k-bg-f8fafc);
    `}
`;

export const HeatRowHead = styled.th`
  ${heatSticky}
  padding: 8px 12px;
  text-align: left;
  font-weight: 500;
  color: ${TEXT};
  white-space: nowrap;
`;

export const HeatCell = styled.td<{
  $tone: number;
  $muted?: boolean;
  $highlight?: boolean;
}>`
  padding: 0;
  min-width: 88px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  background: ${({ $tone }) => heatBg($tone)};
  ${({ $highlight }) =>
    $highlight &&
    css`
      box-shadow: inset 1px 0 0 ${LINE}, inset -1px 0 0 ${LINE};
    `}
  ${({ $muted }) =>
    $muted &&
    css`
      color: var(--k-fg-94a3b8);
      background: var(--k-bg-f8fafc);
    `}
`;

const cellFace = css`
  display: block;
  width: 100%;
  padding: 8px 12px;
  white-space: nowrap;
`;

export const HeatValue = styled.span`
  ${cellFace};
`;

export const HeatEmpty = styled.span`
  ${cellFace};
  color: var(--k-fg-94a3b8);
`;

export const HeatSecondary = styled.span`
  display: block;
  font-size: 11px;
  color: ${MUTED};
`;

export const HeatButton = styled.button`
  ${cellFace};
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: right;
  cursor: pointer;

  &:hover {
    box-shadow: inset 0 0 0 1px var(--k-shadow-93c5fd);
  }

  &:focus-visible {
    outline: 2px solid ${FOCUS};
    outline-offset: -2px;
  }
`;
